import { z } from "zod";

export type ViewerPrefs = {
  versionId: string;
  centerNormalized?: { x: number; y: number };
  zoomRelativeToFit?: number;
  codeSelection?: { startLine: number; endLine: number };
  jsonPath?: string;
};

export function parseUrlFragment(fragment: string, versionId: string): ViewerPrefs | undefined {
  if (!fragment) return undefined;

  // Try parsing as code selection first
  try {
    const codeSelection = z.decode(codeSelectionCodec, fragment);
    return { versionId, codeSelection };
  } catch {
    // Continue to next parser
  }

  // Try parsing as image view
  try {
    const imageView = z.decode(imageViewCodec, fragment);
    return { versionId, ...imageView };
  } catch {
    // Continue to next parser
  }

  // Try parsing as JSON path
  try {
    const jsonPathSelection = z.decode(jsonPathCodec, fragment);
    return { versionId, jsonPath: jsonPathSelection.jsonPath };
  } catch {
    // Continue to next parser
  }

  return undefined;
}

export function formatUrlFragment(view: ViewerPrefs): string {
  if (view.codeSelection) {
    return z.encode(codeSelectionCodec, view.codeSelection);
  }

  if (view.centerNormalized && view.zoomRelativeToFit) {
    return z.encode(imageViewCodec, {
      centerNormalized: view.centerNormalized,
      zoomRelativeToFit: view.zoomRelativeToFit,
    });
  }

  if (view.jsonPath) {
    return z.encode(jsonPathCodec, { jsonPath: view.jsonPath });
  }

  return "";
}

const codeSelectionCodec = z.codec(
  z.string().regex(/^L(\d+)-L(\d+)$/),
  z.object({
    startLine: z.number().int().positive(),
    endLine: z.number().int().positive(),
  }),
  {
    decode: (fragment: string) => {
      const match = fragment.match(/^L(\d+)-L(\d+)$/);
      if (!match) throw new Error("Invalid code selection fragment");
      return {
        startLine: parseInt(match[1], 10),
        endLine: parseInt(match[2], 10),
      };
    },
    encode: (selection: { startLine: number; endLine: number }) => {
      return `L${selection.startLine}-L${selection.endLine}`;
    },
  },
);

const imageViewCodec = z.codec(
  z.string().regex(/^x([\d.]+)y([\d.]+)z([\d.]+)$/),
  z.object({
    centerNormalized: z.object({
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
    }),
    zoomRelativeToFit: z.number().positive(),
  }),
  {
    decode: (fragment: string) => {
      const match = fragment.match(/^x([\d.]+)y([\d.]+)z([\d.]+)$/);
      if (!match) throw new Error("Invalid image view fragment");
      return {
        centerNormalized: {
          x: parseFloat(match[1]),
          y: parseFloat(match[2]),
        },
        zoomRelativeToFit: parseFloat(match[3]),
      };
    },
    encode: (view: { centerNormalized: { x: number; y: number }; zoomRelativeToFit: number }) => {
      const x = Math.round(view.centerNormalized.x * 100) / 100;
      const y = Math.round(view.centerNormalized.y * 100) / 100;
      const z = Math.round(view.zoomRelativeToFit * 100) / 100;
      return `x${x}y${y}z${z}`;
    },
  },
);

const jsonPathCodec = z.codec(
  z.string().regex(/^jp=(.+)$/),
  z.object({
    jsonPath: z.string(),
  }),
  {
    decode: (fragment: string) => {
      const match = fragment.match(/^jp=(.+)$/);
      if (!match) throw new Error("Invalid JSON path fragment");
      return {
        jsonPath: decodeURIComponent(match[1]),
      };
    },
    encode: (selection: { jsonPath: string }) => {
      return `jp=${encodeURIComponent(selection.jsonPath)}`;
    },
  },
);
